import React, { useState } from "react";
import StarBorderRoundedIcon from "@mui/icons-material/StarBorderRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import UnfoldMoreRoundedIcon from "@mui/icons-material/UnfoldMoreRounded";
import {
    formatPointsPerGame,
    formatSavePct,
    formatStatValue,
} from "../../utils/blindDraft";

const SORTABLE_COLUMNS = [
    { key: "starred", label: "★", ariaLabel: "Starred" },
    { key: "fakeName", label: "Player" },
    { key: "position", label: "Pos" },
    { key: "country", label: "Country" },
    { key: "gamesPlayed", label: "GP" },
    { key: "points", label: "PTS" },
    { key: "pointsPerGame", label: "PPG" },
    { key: "savePercentage", label: "SV%" },
    { key: "goalsAgainstAverage", label: "GAA" },
];

function SortableHeader({ column, sortConfig, onSort }) {
    const isActive = sortConfig.key === column.key;
    const direction = isActive ? sortConfig.direction : null;

    return (
        <button
            type="button"
            className={`blind-draft-sort-btn ${isActive ? "is-active" : ""}`}
            onClick={() => onSort(column.key)}
            aria-label={
                isActive
                    ? `Sort by ${column.ariaLabel || column.label}, ${direction === "asc" ? "ascending" : "descending"}`
                    : `Sort by ${column.ariaLabel || column.label}`
            }
        >
            <span>{column.label}</span>
            <span className="blind-draft-sort-icon" aria-hidden="true">
                {!isActive && <UnfoldMoreRoundedIcon fontSize="inherit" />}
                {isActive && direction === "asc" && <ArrowUpwardRoundedIcon fontSize="inherit" />}
                {isActive && direction === "desc" && <ArrowDownwardRoundedIcon fontSize="inherit" />}
            </span>
        </button>
    );
}

function ProspectStatsBreakdown({ breakdown, isGoalie }) {
    if (!breakdown?.length) {
        return <p className="blind-draft-table-empty">No league breakdown available.</p>;
    }

    return (
        <table className="blind-draft-stats-table blind-draft-stats-table--nested">
            <thead>
                <tr>
                    <th>League</th>
                    <th>GP</th>
                    {isGoalie ? (
                        <>
                            <th>W-L-T</th>
                            <th>GAA</th>
                            <th>SV%</th>
                            <th>SO</th>
                        </>
                    ) : (
                        <>
                            <th>G</th>
                            <th>A</th>
                            <th>PTS</th>
                            <th>PPG</th>
                        </>
                    )}
                </tr>
            </thead>
            <tbody>
                {breakdown.map((row) => {
                    const ppg = row.gamesPlayed ? (row.points / row.gamesPlayed).toFixed(2) : "—";
                    const key = `${row.leagueName}-${row.isPlayoff ? "po" : "reg"}`;
                    return (
                        <tr key={key}>
                            <td>
                                {formatStatValue(row.leagueName)}
                                {row.isPlayoff ? " (PO)" : ""}
                            </td>
                            <td>{formatStatValue(row.gamesPlayed)}</td>
                            {isGoalie ? (
                                <>
                                    <td>{formatStatValue(row.record)}</td>
                                    <td>{formatStatValue(row.goalsAgainstAverage)}</td>
                                    <td>{formatSavePct(row.savePercentage)}</td>
                                    <td>{formatStatValue(row.shutouts)}</td>
                                </>
                            ) : (
                                <>
                                    <td>{formatStatValue(row.goals)}</td>
                                    <td>{formatStatValue(row.assists)}</td>
                                    <td>{row.points}</td>
                                    <td>{ppg}</td>
                                </>
                            )}
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
}

function ProspectStarButton({ starred, onToggle, label }) {
    return (
        <button
            type="button"
            className={`blind-draft-star-btn ${starred ? "is-starred" : ""}`}
            aria-label={starred ? `Unstar ${label}` : `Star ${label}`}
            onClick={(event) => {
                event.stopPropagation();
                onToggle();
            }}
        >
            {starred ? (
                <StarRoundedIcon fontSize="small" />
            ) : (
                <StarBorderRoundedIcon fontSize="small" />
            )}
        </button>
    );
}

function ProspectCard({
    prospect,
    drafted,
    starred,
    onToggleStar,
    onClick,
    style,
    className = "",
}) {
    const totals = prospect.draftTotals;
    let statLine = "No stats";
    if (totals) {
        if (prospect.isGoalie) {
            statLine = `${totals.gamesPlayed || 0} GP · ${formatSavePct(totals.savePercentage)} SV% · ${formatStatValue(totals.goalsAgainstAverage?.toFixed?.(2) ?? totals.goalsAgainstAverage)} GAA`;
        } else {
            statLine = `${totals.points || 0} PTS · ${formatPointsPerGame(totals.pointsPerGame)} PPG · ${totals.gamesPlayed || 0} GP`;
        }
    }

    return (
        <button
            type="button"
            className={`blind-draft-card ${drafted ? "is-drafted" : ""} ${starred ? "is-starred" : ""} ${className}`}
            style={style}
            onClick={onClick}
            disabled={drafted}
        >
            <div className="blind-draft-card-top">
                <p className="blind-draft-card-name">{prospect.fakeName}</p>
                <ProspectStarButton
                    starred={starred}
                    onToggle={onToggleStar}
                    label={prospect.fakeName}
                />
            </div>
            <div className="blind-draft-card-meta">
                <span className="blind-draft-badge">{prospect.position}</span>
                <span>{prospect.country}</span>
                {starred && <span className="blind-draft-watch-label">Watchlist</span>}
            </div>
            <p className="blind-draft-stat-line">{statLine}</p>
        </button>
    );
}

function ProspectTableRow({
    prospect,
    drafted,
    starred,
    onToggleStar,
    onOpen,
    onDraft,
    canDraft,
}) {
    const [expanded, setExpanded] = useState(false);
    const totals = prospect.draftTotals;
    const columnCount = canDraft ? 11 : 10;

    return (
        <>
            <tr
                className={`blind-draft-table-row ${drafted ? "is-drafted" : ""} ${starred ? "is-starred" : ""}`}
            >
                <td className="blind-draft-table-star">
                    <ProspectStarButton
                        starred={starred}
                        onToggle={onToggleStar}
                        label={prospect.fakeName}
                    />
                </td>
                <td>
                    <button
                        type="button"
                        className="blind-draft-expand-btn"
                        aria-label={expanded ? "Collapse stats" : "Expand stats"}
                        onClick={(event) => {
                            event.stopPropagation();
                            setExpanded((value) => !value);
                        }}
                    >
                        {expanded ? (
                            <KeyboardArrowUpRoundedIcon fontSize="small" />
                        ) : (
                            <KeyboardArrowDownRoundedIcon fontSize="small" />
                        )}
                    </button>
                </td>
                <td>
                    <button type="button" className="blind-draft-name-btn" onClick={() => onOpen(prospect)}>
                        {prospect.fakeName}
                        {starred && <span className="blind-draft-watch-label">Watchlist</span>}
                    </button>
                </td>
                <td>{prospect.position}</td>
                <td>{prospect.country}</td>
                <td>{formatStatValue(totals?.gamesPlayed)}</td>
                <td>{prospect.isGoalie ? "—" : formatStatValue(totals?.points)}</td>
                <td>{prospect.isGoalie ? "—" : formatPointsPerGame(totals?.pointsPerGame)}</td>
                <td>{prospect.isGoalie ? formatSavePct(totals?.savePercentage) : "—"}</td>
                <td>
                    {prospect.isGoalie
                        ? formatStatValue(totals?.goalsAgainstAverage?.toFixed?.(2) ?? totals?.goalsAgainstAverage)
                        : "—"}
                </td>
                {canDraft && (
                    <td>
                        <button
                            type="button"
                            className="blind-draft-draft-row-btn"
                            disabled={drafted}
                            onClick={(event) => {
                                event.stopPropagation();
                                onDraft(prospect);
                            }}
                        >
                            Draft
                        </button>
                    </td>
                )}
            </tr>
            {expanded && (
                <tr className="blind-draft-table-expand-row">
                    <td colSpan={columnCount}>
                        <ProspectStatsBreakdown
                            breakdown={totals?.leagueBreakdown}
                            isGoalie={prospect.isGoalie}
                        />
                    </td>
                </tr>
            )}
        </>
    );
}

function ProspectTable({
    prospects,
    draftedIds,
    starredIds,
    sortConfig,
    onSort,
    onToggleStar,
    onOpen,
    onDraft,
    canDraft,
}) {
    const columnCount = canDraft ? 11 : 10;

    return (
        <div className="blind-draft-table-wrap">
            <table className="blind-draft-prospects-table">
                <thead>
                    <tr>
                        <th aria-label="Star">
                            <SortableHeader
                                column={SORTABLE_COLUMNS[0]}
                                sortConfig={sortConfig}
                                onSort={onSort}
                            />
                        </th>
                        <th aria-label="Expand" />
                        {SORTABLE_COLUMNS.slice(1).map((column) => (
                            <th key={column.key}>
                                <SortableHeader
                                    column={column}
                                    sortConfig={sortConfig}
                                    onSort={onSort}
                                />
                            </th>
                        ))}
                        {canDraft && <th aria-label="Draft" />}
                    </tr>
                </thead>
                <tbody>
                    {prospects.length === 0 ? (
                        <tr>
                            <td colSpan={columnCount} className="blind-draft-table-empty-cell">
                                No prospects match your filters.
                            </td>
                        </tr>
                    ) : (
                        prospects.map((prospect) => (
                            <ProspectTableRow
                                key={prospect.anonId}
                                prospect={prospect}
                                drafted={draftedIds.includes(prospect.anonId)}
                                starred={starredIds.has(prospect.anonId)}
                                onToggleStar={() => onToggleStar(prospect.anonId)}
                                onOpen={onOpen}
                                onDraft={onDraft}
                                canDraft={canDraft}
                            />
                        ))
                    )}
                </tbody>
            </table>
        </div>
    );
}

function ProspectListFilters({
    listFilters,
    onListFiltersChange,
    countries,
    positions,
    leagues,
    resultCount,
    onClearFilters,
}) {
    const updateFilter = (key, value) => {
        onListFiltersChange({ ...listFilters, [key]: value });
    };

    const hasActiveFilters = (
        listFilters.country !== "ALL"
        || listFilters.position !== "ALL"
        || listFilters.type !== "ALL"
        || listFilters.starred !== "ALL"
        || listFilters.league !== "ALL"
        || listFilters.minGp !== ""
        || listFilters.minPts !== ""
        || listFilters.minPpg !== ""
        || listFilters.minSv !== ""
        || listFilters.maxGaa !== ""
    );

    return (
        <div className="blind-draft-list-filters">
            <div className="blind-draft-list-filters-grid">
                <label className="blind-draft-filter-field">
                    <span>League</span>
                    <select
                        className="blind-draft-filter"
                        value={listFilters.league}
                        onChange={(e) => updateFilter("league", e.target.value)}
                    >
                        {leagues.map((league) => (
                            <option key={league} value={league}>{league === "ALL" ? "Any league" : league}</option>
                        ))}
                    </select>
                </label>
                <label className="blind-draft-filter-field">
                    <span>League filter</span>
                    <select
                        className="blind-draft-filter"
                        value={listFilters.leaguePlayed}
                        onChange={(e) => updateFilter("leaguePlayed", e.target.value)}
                        disabled={listFilters.league === "ALL"}
                    >
                        <option value="YES">Played in league</option>
                        <option value="NO">Did not play in league</option>
                    </select>
                </label>
                <label className="blind-draft-filter-field">
                    <span>Country</span>
                    <select
                        className="blind-draft-filter"
                        value={listFilters.country}
                        onChange={(e) => updateFilter("country", e.target.value)}
                    >
                        {countries.map((country) => (
                            <option key={country} value={country}>{country}</option>
                        ))}
                    </select>
                </label>
                <label className="blind-draft-filter-field">
                    <span>Position</span>
                    <select
                        className="blind-draft-filter"
                        value={listFilters.position}
                        onChange={(e) => updateFilter("position", e.target.value)}
                    >
                        {positions.map((position) => (
                            <option key={position} value={position}>{position}</option>
                        ))}
                    </select>
                </label>
                <label className="blind-draft-filter-field">
                    <span>Type</span>
                    <select
                        className="blind-draft-filter"
                        value={listFilters.type}
                        onChange={(e) => updateFilter("type", e.target.value)}
                    >
                        <option value="ALL">All</option>
                        <option value="SKATER">Skaters</option>
                        <option value="GOALIE">Goalies</option>
                    </select>
                </label>
                <label className="blind-draft-filter-field">
                    <span>Watchlist</span>
                    <select
                        className="blind-draft-filter"
                        value={listFilters.starred}
                        onChange={(e) => updateFilter("starred", e.target.value)}
                    >
                        <option value="ALL">All</option>
                        <option value="STARRED">Starred only</option>
                    </select>
                </label>
                <label className="blind-draft-filter-field">
                    <span>Min GP</span>
                    <input
                        className="blind-draft-filter blind-draft-filter--number"
                        type="number"
                        min="0"
                        placeholder="Any"
                        value={listFilters.minGp}
                        onChange={(e) => updateFilter("minGp", e.target.value)}
                    />
                </label>
                <label className="blind-draft-filter-field">
                    <span>Min PTS</span>
                    <input
                        className="blind-draft-filter blind-draft-filter--number"
                        type="number"
                        min="0"
                        placeholder="Any"
                        value={listFilters.minPts}
                        onChange={(e) => updateFilter("minPts", e.target.value)}
                    />
                </label>
                <label className="blind-draft-filter-field">
                    <span>Min PPG</span>
                    <input
                        className="blind-draft-filter blind-draft-filter--number"
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="Any"
                        value={listFilters.minPpg}
                        onChange={(e) => updateFilter("minPpg", e.target.value)}
                    />
                </label>
                <label className="blind-draft-filter-field">
                    <span>Min SV%</span>
                    <input
                        className="blind-draft-filter blind-draft-filter--number"
                        type="number"
                        min="0"
                        max="1"
                        step="0.001"
                        placeholder="Any"
                        value={listFilters.minSv}
                        onChange={(e) => updateFilter("minSv", e.target.value)}
                    />
                </label>
                <label className="blind-draft-filter-field">
                    <span>Max GAA</span>
                    <input
                        className="blind-draft-filter blind-draft-filter--number"
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="Any"
                        value={listFilters.maxGaa}
                        onChange={(e) => updateFilter("maxGaa", e.target.value)}
                    />
                </label>
            </div>
            <div className="blind-draft-list-filters-footer">
                <span className="blind-draft-list-filters-count">
                    Showing {resultCount} player{resultCount === 1 ? "" : "s"}
                </span>
                {hasActiveFilters && (
                    <button type="button" className="blind-draft-secondary-btn" onClick={onClearFilters}>
                        Clear filters
                    </button>
                )}
            </div>
        </div>
    );
}

function ProspectBoard({
    prospects,
    viewMode,
    draftedIds = [],
    starredIds,
    sortConfig,
    onSort,
    listFilters,
    onListFiltersChange,
    countries,
    positions,
    leagues = ["ALL"],
    onClearListFilters,
    onToggleStar,
    onOpen,
    onDraft,
    canDraft = false,
}) {
    if (viewMode === "list") {
        return (
            <>
                <ProspectListFilters
                    listFilters={listFilters}
                    onListFiltersChange={onListFiltersChange}
                    countries={countries}
                    positions={positions}
                    leagues={leagues}
                    resultCount={prospects.length}
                    onClearFilters={onClearListFilters}
                />
                <ProspectTable
                    prospects={prospects}
                    draftedIds={draftedIds}
                    starredIds={starredIds}
                    sortConfig={sortConfig}
                    onSort={onSort}
                    onToggleStar={onToggleStar}
                    onOpen={onOpen}
                    onDraft={onDraft}
                    canDraft={canDraft}
                />
            </>
        );
    }

    return (
        <div className="blind-draft-grid">
            {prospects.map((prospect, index) => (
                <ProspectCard
                    key={prospect.anonId}
                    prospect={prospect}
                    drafted={draftedIds.includes(prospect.anonId)}
                    starred={starredIds.has(prospect.anonId)}
                    onToggleStar={() => onToggleStar(prospect.anonId)}
                    onClick={() => {
                        if (canDraft) {
                            onDraft(prospect);
                            return;
                        }
                        onOpen(prospect);
                    }}
                    style={{ "--card-index": index }}
                />
            ))}
        </div>
    );
}

export { ProspectStatsBreakdown };
export default ProspectBoard;
